import { TagState, TagAction, TagType, TagInfo } from "./types";
import { calculateTagAnalysis, createInitialTags } from "./tagUtils";

// Fallback initial state (should not be used with init function)
export const initialTagState: TagState = {
  selectedTags: [],
  removedTags: [],
  initialSnapshot: [],
  recentTags: [],
};

export const tagReducer = (state: TagState, action: TagAction): TagState => {
  switch (action.type) {
    case "INITIALIZE": {
      const { selectedRows, recentTags } = action.payload;
      const tagAnalysis = calculateTagAnalysis(selectedRows);
      const initialTags = createInitialTags(tagAnalysis);
      return {
        selectedTags: [...initialTags],
        removedTags: [],
        initialSnapshot: [...initialTags],
        recentTags: recentTags,
      };
    }

    case "ADD_TAG": {
      const tagName = action.payload.trim();
      if (!tagName) return state;

      // Check if tag exists in removed tags first
      const removedTag = state.removedTags.find((t) => t.name === tagName);
      if (removedTag) {
        return {
          ...state,
          removedTags: state.removedTags.filter((t) => t.name !== tagName),
          selectedTags: [...state.selectedTags, removedTag].sort((a, b) =>
            a.name.localeCompare(b.name)
          ),
        };
      }

      // Check if tag already exists
      const exists = state.selectedTags.find((t) => t.name === tagName);
      if (exists) return state;

      const newTag: TagInfo = {
        name: tagName,
        type: TagType.NEW,
        originallyPartial: false,
      };

      return {
        ...state,
        selectedTags: [...state.selectedTags, newTag].sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
      };
    }

    case "REMOVE_TAG": {
      const tagName = action.payload;
      const tagToRemove = state.selectedTags.find((t) => t.name === tagName);

      if (!tagToRemove) return state;

      const newState = {
        ...state,
        selectedTags: state.selectedTags.filter((t) => t.name !== tagName),
      };

      // Only add to removed tags if it's not a newly added tag
      if (tagToRemove.type !== TagType.NEW) {
        const exists = state.removedTags.find((t) => t.name === tagName);
        if (!exists) {
          newState.removedTags = [...state.removedTags, tagToRemove].sort(
            (a, b) => a.name.localeCompare(b.name)
          );
        }
      }

      return newState;
    }

    case "RESTORE_TAG": {
      const tagName = action.payload;
      const tagToRestore = state.removedTags.find((t) => t.name === tagName);

      if (!tagToRestore) return state;

      // Don't add duplicate tags
      const exists = state.selectedTags.find((t) => t.name === tagName);
      if (exists) return state;

      return {
        ...state,
        removedTags: state.removedTags.filter((t) => t.name !== tagName),
        selectedTags: [...state.selectedTags, tagToRestore].sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
      };
    }

    case "PROMOTE_TAG": {
      const tagName = action.payload;
      return {
        ...state,
        selectedTags: state.selectedTags.map((tag) => {
          if (tag.name === tagName) {
            if (tag.type === TagType.PARTIAL) {
              return { ...tag, type: TagType.ALL };
            } else if (tag.type === TagType.ALL && tag.originallyPartial) {
              return { ...tag, type: TagType.PARTIAL };
            }
          }
          return tag;
        }),
      };
    }

    case "REMOVE_ALL_TAGS": {
      const newRemovedTags = [...state.removedTags];
      state.selectedTags.forEach((tag) => {
        const exists = newRemovedTags.find((t) => t.name === tag.name);
        if (!exists && tag.type !== TagType.NEW) {
          newRemovedTags.push(tag);
        }
      });

      return {
        ...state,
        selectedTags: [],
        removedTags: newRemovedTags.sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
      };
    }

    default:
      return state;
  }
};
